---
id: k8s-troubleshooting
title: Kubernetes Pod Troubleshooting
category: containers
difficulty: intermediate
tags:
  - kubernetes
  - k8s
  - pods
  - debugging
  - crashloopbackoff
  - troubleshooting
description: >
  Systematically diagnoses Kubernetes pod issues using describe output,
  events, and logs.
whenToUse: >
  When pods are in CrashLoopBackOff, Pending, or Error state and you
  need structured diagnosis.
variables:
  - name: kubectl_describe
    desc: Output of kubectl describe pod <name>
  - name: pod_logs
    desc: Output of kubectl logs <pod> (include --previous if CrashLoop)
  - name: k8s_version
    desc: Kubernetes cluster version
  - name: namespace
    desc: Namespace the pod is in
  - name: node_info
    desc: Node pool details (instance type, capacity)
  - name: recent_changes
    desc: Recent deployments or cluster changes
tips:
  - Always include --previous flag for logs if the pod is in CrashLoopBackOff.
  - Check events at both pod and namespace level — some issues show in namespace events.
  - Include node describe output if the issue might be node-level (disk pressure, memory pressure).
---

You are a Kubernetes expert troubleshooting pod issues.

## Pod Status
```
{{kubectl_describe}}
```

## Pod Logs
```
{{pod_logs}}
```

## Cluster Context
- K8s version: {{k8s_version}}
- Namespace: {{namespace}}
- Node pool info: {{node_info}}
- Recent changes: {{recent_changes}}

## Task
1. Identify the root cause from the describe output and logs
2. Classify the issue category:
   - Scheduling (resource constraints, affinity, taints)
   - Image (pull errors, wrong tag, registry auth)
   - Configuration (env vars, secrets, configmaps)
   - Application (crash, OOM, health check failure)
   - Networking (DNS, service mesh, network policy)
   - Storage (PVC, mount errors)
3. Provide step-by-step fix
4. Suggest preventive measures

## Output Format (JSON)
{
  "diagnosis": {
    "root_cause": "one-sentence summary",
    "category": "scheduling | image | configuration | application | networking | storage",
    "evidence": ["specific lines from describe/logs that prove this"],
    "severity": "critical | high | medium"
  },
  "fix": {
    "immediate": ["step 1 with kubectl command", "step 2"],
    "yaml_changes": "any manifest changes needed"
  },
  "preventive": ["measure 1", "measure 2"],
  "related_resources_to_check": ["other resources that might be affected"]
}

<!-- EXAMPLE_OUTPUT -->

{
  "diagnosis": {
    "root_cause": "OOMKilled — container memory limit (256Mi) is too low for the Java application",
    "category": "application",
    "evidence": [
      "Last State: Terminated, Reason: OOMKilled, Exit Code: 137",
      "Restart Count: 5"
    ],
    "severity": "high"
  },
  "fix": {
    "immediate": ["kubectl set resources deploy/app -c app --limits=memory=512Mi -n default"],
    "yaml_changes": "Update resources.limits.memory to 512Mi in deployment manifest"
  }
}
